import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type { IChecklistTask } from "./types";

interface IState {
  userId: string | null;
  panelOpen: boolean;
  completed: string[];
}

interface IActions {
  hydrate: (userId: string) => void;
  markDone: (key: string) => void;
  setCompleted: (keys: string[]) => void;
  setPanelOpen: (open: boolean) => void;
  togglePanel: () => void;
}

export type TChecklistStore = IState & IActions;

interface IOptions {
  /** `false` keeps completion in memory only; the caller hydrates it from the server. */
  persist?: boolean;
  /**
   * Called once per key when it first becomes done for this user in this browser: not for
   * defaults, nor for keys a previous session already persisted. Needs `persist`.
   */
  onDone?: (key: string, completed: string[]) => void;
}

export const createChecklistStore = (
  storagePrefix: string,
  tasks: IChecklistTask[],
  { persist = true, onDone }: IOptions = {},
) => {
  /** Keys retired by a later release must not keep inflating the counter. */
  const KNOWN_KEYS = new Set(tasks.map((t) => t.key));
  const storageKey = (userId: string) => `${storagePrefix}${userId}`;

  const readPersisted = (userId: string): string[] => {
    if (!persist) return [];
    try {
      const raw = window.localStorage.getItem(storageKey(userId));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      // v1 stored a bare array; an interim build stored { completed, … }.
      const completedRaw = Array.isArray(parsed) ? parsed : parsed?.completed;
      if (!Array.isArray(completedRaw)) return [];
      return completedRaw.filter(
        (k): k is string => typeof k === "string" && KNOWN_KEYS.has(k),
      );
    } catch {
      return [];
    }
  };

  const writePersisted = (userId: string, keys: string[]) => {
    if (!persist) return;
    try {
      window.localStorage.setItem(storageKey(userId), JSON.stringify(keys));
    } catch {
      // Persistence is best-effort; a full or blocked store must not break the UI.
    }
  };

  const defaultCompleted = () =>
    tasks.filter((t) => t.completedByDefault).map((t) => t.key);

  const useStore = create<TChecklistStore>((set, get) => ({
    userId: null,
    panelOpen: false,
    completed: defaultCompleted(),

    hydrate: (userId) => {
      const { userId: prevUserId, completed } = get();
      if (prevUserId === userId) return;
      // Trackers arm before the current-user query resolves, so anything
      // completed in that window lives only in memory. Carry it across on the
      // first hydration — but not on an account switch.
      const carryOver = prevUserId === null ? completed : [];
      const persisted = readPersisted(userId);
      const merged = Array.from(
        new Set([...defaultCompleted(), ...persisted, ...carryOver]),
      );
      set({ userId, completed: merged });
      // Only persist if carryOver actually introduces a key that wasn't
      // already known — a completedByDefault task carrying over from the
      // pre-hydration in-memory state is not new information.
      const known = new Set([...defaultCompleted(), ...persisted]);
      const newCarryOver = carryOver.filter((k) => !known.has(k));
      if (newCarryOver.length) writePersisted(userId, merged);
      for (const key of newCarryOver) onDone?.(key, merged);
    },

    markDone: (key) => {
      const { completed, userId } = get();
      if (!KNOWN_KEYS.has(key) || completed.includes(key)) return;
      const next = [...completed, key];
      set({ completed: next });
      // Before hydration, hydrate() reports it as a new carry-over key instead.
      if (!userId) return;
      // Re-read and union on every write so a second tab can never clobber a
      // completion this tab has not seen.
      const persisted = readPersisted(userId);
      if (!persisted.includes(key)) onDone?.(key, next);
      const merged = Array.from(new Set([...persisted, ...next]));
      writePersisted(userId, merged);
    },

    setCompleted: (keys) => {
      const known = keys.filter((k) => KNOWN_KEYS.has(k));
      set({
        completed: Array.from(new Set([...defaultCompleted(), ...known])),
      });
    },

    setPanelOpen: (panelOpen) => set({ panelOpen }),
    togglePanel: () => set((s) => ({ panelOpen: !s.panelOpen })),
  }));

  const useStoreShallow = <U>(selector: (s: TChecklistStore) => U) =>
    useStore(useShallow(selector));

  return {
    useStore,
    useStoreShallow,
    total: tasks.length,
    /** Imperative entry point for trackers that live outside React. */
    markDone: (key: string) => useStore.getState().markDone(key),
    isDone: (key: string) => useStore.getState().completed.includes(key),
  };
};
